import dayjs from 'dayjs'
import { CERTIFICATION_IDENTITY_VALIDATION_MESSAGES } from '@/constants/certifications'
import { convertHoursToMinutes, convertMinutesToHours } from '@/utils/utils'

export function stringifyCertificationFormValue(value) {
  return value === undefined || value === null ? '' : `${value}`
}

export function normalizeCertificationFormDate(value) {
  if (!value) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.toDate() : null
}

export function normalizeCertificationDateInputValue(value) {
  if (!value) return ''
  return String(value).slice(0, 10)
}

export function normalizeCertificationMarkdownValue(value) {
  if (value === undefined || value === null) return ''
  return String(value).replace(/\r\n/g, '\n')
}

export function normalizeCertificationLearningPaths(learningPaths) {
  if (!Array.isArray(learningPaths)) return []

  return learningPaths
    .map((learningPath) =>
      learningPath && typeof learningPath === 'object'
        ? learningPath.id
        : learningPath
    )
    .filter(
      (learningPathId) =>
        learningPathId !== undefined &&
        learningPathId !== null &&
        learningPathId !== ''
    )
}

export function buildPersistedCertificationVersion(certificationVersion = {}) {
  const source = certificationVersion || {}
  const voucherConfig = source.voucher_config || {}
  const learningPaths = Array.isArray(source.learning_paths)
    ? source.learning_paths
    : []

  return {
    ...source,
    voucher_config: { ...voucherConfig },
    learning_paths: [...learningPaths],
  }
}

export function buildCertificationFormSnapshot(
  certificationVersion = {},
  { isCreationMode = false } = {}
) {
  const source = isCreationMode ? {} : certificationVersion || {}
  const certification = source.certification || {}
  const voucherConfig = source.voucher_config || {}
  const learningPaths = Array.isArray(source.learning_paths)
    ? source.learning_paths
    : []

  return {
    certificationName: isCreationMode ? '' : certification.name || '',
    certificationShortName: isCreationMode ? '' : certification.short_name || '',
    learningArea: certification.learning_area_id,
    difficulty: certification.difficulty,
    maxAttempts: stringifyCertificationFormValue(voucherConfig.max_attempts),
    nextAttemptTime: stringifyCertificationFormValue(
      convertMinutesToHours(voucherConfig.time_after_first_attempt_in_minutes)
    ),
    voucherStartDate: normalizeCertificationFormDate(
      voucherConfig.voucher_start_date
    ),
    validForInDays: stringifyCertificationFormValue(
      voucherConfig.valid_for_in_days
    ),
    accredibleGroupId: stringifyCertificationFormValue(source.accredible_group_id),
    retiresAt: normalizeCertificationDateInputValue(source.retires_at),
    learningPaths: normalizeCertificationLearningPaths(learningPaths),
    recommendationsMd: normalizeCertificationMarkdownValue(
      source.recommendations_md
    ),
    instructionsMd: normalizeCertificationMarkdownValue(source.instructions_md),
  }
}

export function buildCertificationFormChangeSnapshot(form = {}) {
  return {
    certificationName: form.certificationName,
    certificationShortName: form.certificationShortName,
    learningArea: form.learningArea,
    difficulty: form.difficulty,
    maxAttempts: form.maxAttempts,
    nextAttemptTime: form.nextAttemptTime,
    voucherStartDate: form.voucherStartDate,
    validForInDays: form.validForInDays,
    accredibleGroupId: form.accredibleGroupId,
    retiresAt: form.retiresAt,
    learningPaths: normalizeCertificationLearningPaths(form.learningPaths),
    recommendationsMd: form.recommendationsMd,
    instructionsMd: form.instructionsMd,
  }
}

export function areCertificationFormSnapshotValuesEqual(leftValue, rightValue) {
  if (leftValue instanceof Date && rightValue instanceof Date) {
    return leftValue.getTime() === rightValue.getTime()
  }
  if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
    const sortedLeft = [...normalizeCertificationLearningPaths(leftValue)].sort()
    const sortedRight = [...normalizeCertificationLearningPaths(rightValue)].sort()

    return (
      sortedLeft.length === sortedRight.length &&
      sortedLeft.every((value, index) => value === sortedRight[index])
    )
  }
  return leftValue === rightValue
}

export function getCertificationIdentityValidationErrors({
  name,
  shortName,
  learningArea,
} = {}) {
  const errors = {}
  if (!String(name || '').trim()) {
    errors.certificationName =
      CERTIFICATION_IDENTITY_VALIDATION_MESSAGES.NAME_REQUIRED
  }
  if (!String(shortName || '').trim()) {
    errors.certificationShortName =
      CERTIFICATION_IDENTITY_VALIDATION_MESSAGES.SHORT_NAME_REQUIRED
  }
  if (!String(learningArea || '').trim()) {
    errors.learningArea =
      CERTIFICATION_IDENTITY_VALIDATION_MESSAGES.LEARNING_AREA_REQUIRED
  }
  return errors
}

export function mapCertificationIdentityBackendErrors(error) {
  const response = error && error.response
  if (!response || response.status !== 400 || !response.data) return {}

  const errors = {}
  if (Array.isArray(response.data.name) && response.data.name.length) {
    errors.certificationName = response.data.name[0]
  }
  if (Array.isArray(response.data.short_name) && response.data.short_name.length) {
    errors.certificationShortName = response.data.short_name[0]
  }
  return errors
}

export function buildCertificationVersionUpdatePayload({
  certificationVersion = {},
  form = {},
  status,
  includeStatus = true,
} = {}) {
  const source = certificationVersion || {}
  const { id, ...otherFields } = source
  const payload = {
    certificationVersionId: id,
    form: {
      certification_short_name: form.certificationShortName,
      max_attempts: form.maxAttempts,
      time_after_first_attempt_in_minutes: convertHoursToMinutes(
        form.nextAttemptTime
      ),
      voucher_start_date: form.voucherStartDate,
      valid_for_in_days: form.validForInDays,
      accredible_group_id: form.accredibleGroupId,
      retires_at: form.retiresAt,
      learning_paths: normalizeCertificationLearningPaths(form.learningPaths),
      recommendations_md: form.recommendationsMd,
      instructions_md: form.instructionsMd,
    },
    certificationVersion: otherFields,
  }
  if (includeStatus) {
    payload.form.status = status
  }
  return payload
}

export function buildCertificationIdentityPatch(form = {}, snapshot = {}) {
  const payload = {}
  if (form.certificationName !== snapshot.certificationName) {
    payload.name = form.certificationName
  }
  if (form.certificationShortName !== snapshot.certificationShortName) {
    payload.short_name = form.certificationShortName
  }
  if (form.learningArea !== snapshot.learningArea) {
    payload.learning_area_id = form.learningArea
  }
  if (form.difficulty !== snapshot.difficulty) {
    payload.difficulty = form.difficulty
  }
  return payload
}

export function buildCreationCertificationPayload(form = {}) {
  const payload = {
    name: form.certificationName,
    short_name: form.certificationShortName,
  }
  const optionalFields = {
    difficulty: form.difficulty,
    learning_area_id: form.learningArea,
  }

  Object.entries(optionalFields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      payload[key] = value
    }
  })

  return payload
}

export function buildCreationCertificationVersionPayload(form = {}) {
  const payload = {}
  const voucherConfig = {
    max_attempts: form.maxAttempts,
    time_after_first_attempt_in_minutes: convertHoursToMinutes(
      form.nextAttemptTime
    ),
    valid_for_in_days: form.validForInDays,
  }

  const normalizedVoucherConfig = Object.entries(voucherConfig).reduce(
    (config, [key, value]) => {
      if (value === undefined || value === null || value === '') {
        return config
      }

      const numericValue = Number(value)
      if (Number.isNaN(numericValue)) return config

      config[key] = numericValue
      return config
    },
    {}
  )

  if (Object.keys(normalizedVoucherConfig).length) {
    payload.voucher_config = normalizedVoucherConfig
  }
  if (form.instructionsMd) {
    payload.instructions_md = form.instructionsMd
  }
  if (form.recommendationsMd) {
    payload.recommendations_md = form.recommendationsMd
  }
  if (form.accredibleGroupId) {
    payload.accredible_group_id = form.accredibleGroupId
  }
  const learningPaths = normalizeCertificationLearningPaths(form.learningPaths)
  if (learningPaths.length) {
    payload.learning_paths = learningPaths
  }

  return payload
}
